import { motion } from "framer-motion";
import { PartyPopper, Users, Calendar, Sparkles } from "lucide-react";

const PrivatePartiesSection = ({ onBookClick }) => {
  const champagneImage = "https://images.unsplash.com/photo-1647905555465-0f9004fbdaed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxjaGFtcGFnbmUlMjB0b2FzdHxlbnwwfHx8fDE3NzI4MTk3NjJ8MA&ixlib=rb-4.1.0&q=85";

  return (
    <section id="private-parties" data-testid="private-parties-section" className="py-20 md:py-32 px-4 md:px-8 lg:px-16 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url(${champagneImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <PartyPopper className="w-10 h-10 text-gold mb-6" />
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
              Private <span className="text-gold">Parties</span>
            </h2>
            <p className="font-accent text-2xl text-rose-light mb-6">
              Perfect for Birthdays & Girls&apos; Night Out
            </p>
            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              Want an exclusive experience just for your crew? Book a private party for your 
              birthday celebration, bachelorette, or any special occasion. Get the full 
              Girls Night experience with your own private session.
            </p>

            {/* Features */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">Up to 15 Guests</p>
                  <p className="text-sm text-zinc-500">Perfect size for an intimate gathering</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">Flexible Scheduling</p>
                  <p className="text-sm text-zinc-500">Choose your preferred date & time</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">VIP Treatment</p>
                  <p className="text-sm text-zinc-500">Exclusive attention & premium materials</p>
                </div>
              </div>
            </div>

            {/* Price & CTA */}
            <div className="glass-card p-6 inline-block mb-8">
              <p className="text-zinc-400 text-sm uppercase tracking-wider mb-2">Starting From</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-gold">$800</span>
                <span className="text-zinc-500">- $1,000</span>
              </div>
              <p className="text-sm text-zinc-500 mt-1">for up to 15 guests</p>
            </div>

            <div className="block">
              <button
                data-testid="book-private-party-btn"
                onClick={onBookClick}
                className="btn-gold"
              >
                Book Private Party
              </button>
            </div>
          </motion.div>

          {/* Image/Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative aspect-square max-w-lg mx-auto">
              <img
                src="https://images.unsplash.com/photo-1669067166033-75781a8e6376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxjaGFtcGFnbmUlMjB0b2FzdHxlbnwwfHx8fDE3NzI4MTk3NjJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Champagne celebration"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border-2 border-gold/30" />
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border border-rose/30" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-gold/30" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PrivatePartiesSection;
